/**
 * The client-side representation of the serialized server state, as produced
 * by `State.Serialize()` in `State.py`. Only the fields the browser must act
 * on are present here — `Data` and `Layout` are omitted because the browser
 * treats them as opaque blobs: it holds them in memory and sends them back to
 * the server on the next request without interpreting their contents.
 *
 * The fields that are present each serve a specific browser-side purpose:
 * - `hasDataChanges` tells the app whether the user has uncommitted edits,
 *   so it can warn before navigation or session reset.
 * - `panes` lists the pane identifiers the server expects the browser to
 *   re-serialize on the next round-trip so their scroll positions, selections,
 *   and other view state survive across requests.
 * - `stateId` is a UUID that lets developers correlate a specific client state
 *   snapshot with its corresponding server-side state during debugging.
 */
export interface State {
  hasDataChanges: boolean;
  panes: string[];
  stateId: string;
}

/**
 * A static singleton that acts as the browser's store for the current server
 * state. After each server round-trip, `setState` is called with the new
 * `State`; between round-trips, other parts of the app read from it.
 */
export default class AppServer {
  private static state: State | null = null;

  /**
   * Registers a pane with the app server by pushing its identifier into the
   * current state's pane list.
   *
   * Panes call this as they render so that the accumulated list can be sent
   * back to the server on the next request, allowing the server to restore each
   * pane's view state (scroll position, selection, etc.) across round-trips.
   */
  public static addPane(pane: string) {
    if (!AppServer.state) {
      throw Error("AppServer state must be set before a pane can be added");
    }

    AppServer.state.panes.push(pane);
  }

  /**
   * Nulls out the current state, ensuring the app is reverted to a clean slate.
   *
   * This is used when navigating to ensure state from a previous layout does
   * not persist into a new presentation.
   */
  public static clearState() {
    AppServer.state = null;
  }

  /**
   * Returns the current server state. The state is most commonly retrieved so
   * it can be sent back to the server as part of the next round-trip request.
   */
  public static getState(): State | null {
    return AppServer.state;
  }

  /**
   * Returns whether the user has uncommitted edits, as reported by the server
   * in the last round-trip.
   *
   * This is used to decide whether to prompt the user before performing an
   * action that would discard their in-progress work, such as navigating away.
   */
  public static hasChanges(): boolean {
    if (AppServer.state === null) {
      return false;
    }

    return AppServer.state.hasDataChanges;
  }

  /**
   * Replaces the current state with the one returned by the server at the end
   * of a round-trip, making the updated data and pane information available to
   * the rest of the app until the next request completes.
   */
  public static setState(state: State | null) {
    AppServer.state = state;
  }
}
